import React from "react";
import accountProvider, {Role} from "../../account/AccountProvider";
import furoApi from "../../api/FuroApi";

export interface AlertController {
  alertCount: number;
  refreshAlertCount: () => void;
}

export interface GlobalAlertContextState {
  refreshAll: () => void;
  item: AlertController;
  maintenance: AlertController;
  event: AlertController;
  checklist: AlertController;
}

const GlobalAlertContext: React.Context<GlobalAlertContextState> = React.createContext({
  refreshAll: () => {},
  item: {
    alertCount: 0,
    refreshAlertCount: () => {},
  },
  maintenance: {
    alertCount: 0,
    refreshAlertCount: () => {},
  },
  event: {
    alertCount: 0,
    refreshAlertCount: () => {},
  },
  checklist: {
    alertCount: 0,
    refreshAlertCount: () => {},
  },
});

export const GlobalAlertContextConsumer = GlobalAlertContext.Consumer;

export class GlobalAlertContextProvider extends React.Component<any, GlobalAlertContextState> {

  public state: GlobalAlertContextState = {
    refreshAll: () => this.refresh(),
    item: {
      alertCount: 0,
      refreshAlertCount: () => this.loadItemAlertCount(),
    },
    maintenance: {
      alertCount: 0,
      refreshAlertCount: () => this.loadItemMaintenanceAlertCount(),
    },
    event: {
      alertCount: 0,
      refreshAlertCount: () => this.loadEventAlertCount(),
    },
    checklist: {
      alertCount: 0,
      refreshAlertCount: () => this.loadChecklistAlertCount(),
    }
  }

  private pollIntervalId: ReturnType<typeof setInterval>;

  public componentDidMount() {
    this.refresh();
    this.pollIntervalId = setInterval(() => this.refresh(), 60000 * 5); // 5 min poll
  }

  public componentWillUnmount() {
    clearInterval(this.pollIntervalId);
  }

  public render() {
    return (
      <GlobalAlertContext.Provider value={this.state}>
        {this.props.children}
      </GlobalAlertContext.Provider>
    );
  }

  private refresh() {
    const role = accountProvider.getRole();
    if (role === Role.ADMIN) {
      this.loadItemAlertCount();
      this.loadItemMaintenanceAlertCount();
      this.loadEventAlertCount();
      this.loadChecklistAlertCount();
    } else if (role === Role.SALES) {
      this.loadEventAlertCount();
      this.loadChecklistAlertCount();
    } else if (role === Role.MAINTENANCE) {
      this.loadItemMaintenanceAlertCount();
      this.loadChecklistAlertCount();
    }
  }

  private loadItemAlertCount() {
    furoApi.alerts.getTriggeredCount()
      .then((count) => this.setItemAlertCount(count))
      .catch(() => this.setItemAlertCount(0));
  }

  private setItemAlertCount(count: number) {
    this.setState((prevState) => {
      const newState: GlobalAlertContextState = {...prevState};
      newState.item.alertCount = count;
      return newState;
    });
  }

  private loadItemMaintenanceAlertCount() {
    furoApi.maintenance.getUnhandledMaintenanceCount()
      .then((count) => this.setItemMaintenanceAlertCount(count))
      .catch(() => this.setItemMaintenanceAlertCount(0));
  }

  private setItemMaintenanceAlertCount(count: number) {
    this.setState((prevState) => {
      const newState: GlobalAlertContextState = {...prevState};
      newState.maintenance.alertCount = count;
      return newState;
    });
  }

  private loadEventAlertCount() {
    furoApi.eventAlerts.getUnhandledEventCount()
      .then((count) => this.setEventAlertCount(count))
      .catch(() => this.setEventAlertCount(0));
  }

  private setEventAlertCount(count: number) {
    this.setState((prevState) => {
      const newState: GlobalAlertContextState = {...prevState};
      newState.event.alertCount = count;
      return newState;
    });
  }

  private loadChecklistAlertCount() {
    furoApi.checklistAlerts.getUnhandledChecklistCount()
      .then((count) => this.setChecklistAlertCount(count))
      .catch(() => this.setChecklistAlertCount(0));
  }

  private setChecklistAlertCount(count: number) {
    this.setState((prevState) => {
      const newState: GlobalAlertContextState = {...prevState};
      newState.checklist.alertCount = count;
      return newState;
    });
  }
}


interface AlertControllerHolder {
  alertController?: AlertController;
}

export const bindAlertController = (controller: AlertController, holder: AlertControllerHolder): React.ReactNode => {
  holder.alertController = controller;
  return null;
}


// WEBPACK FOOTER //
// ./src/app/alert/GlobalAlertContextProvider.tsx